// src/components/EstadisticasSalary.tsx
import React, { useState, useMemo } from 'react';
import { UserSalary } from '../types'; // Ajusta la ruta según tu estructura

// Import de tus componentes hijos
import CardSection from './components/CardSection';
import ChartSection from './components/ChartSection';

interface EstadisticasSalaryProps {
  salaryList: UserSalary[];
}

const EstadisticasSalary: React.FC<EstadisticasSalaryProps> = ({ salaryList }) => {
  // ========================= ESTADOS =========================
  const [showCombined, setShowCombined] = useState(false);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endYear, setEndYear] = useState(new Date().getFullYear());

  // Lista de años para dropdown
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);


  function firstLetterFirstLastName(string: string): string {
      const parts = string.trim().split(' ');
      if (parts.length === 1) {
        // Solo un apellido
        return parts[0].charAt(0);
      } else if (parts.length >= 2) {
        // Dos apellidos o más, solo la primera letra del primero
        return parts[0].charAt(0);
      }
      return '';
  }
  // ========================= FUNCIONES AUX =========================
  const parseMonthYear = (my?: string) => {
    if (!my) return { month: 0, year: 0 };
    const [mm, yy] = my.split('/');
    const month = parseInt(mm, 10);
    const year = 2000 + parseInt(yy, 10);
    return { month, year };
  };

  // ========================= FILTRADO =========================
  const filteredSalaries = salaryList.filter((salary) => {
    if (!salary.month_year) return false;
    const { year } = parseMonthYear(salary.month_year);
    return year >= startYear && year <= endYear;
  });

  // ========================= EJE X (distinct month_year) =========================
  const monthYearSet = new Set<string>();
  filteredSalaries.forEach((salary) => {
    if (salary.month_year) {
      monthYearSet.add(salary.month_year);
    }
  });

  const distinctMonthYears = Array.from(monthYearSet)
    .map((my) => {
      const { month, year } = parseMonthYear(my);
      return {
        raw: my,
        month,
        year,
        dateValue: new Date(year, month - 1).getTime(),
      };
    })
    .sort((a, b) => a.dateValue - b.dateValue);

  const xAxisValues = distinctMonthYears.map((item) => item.raw);

  // ========================= AGRUPAR POR NOMBRE COMPLETO =========================
  const groupByFullName = (salaries: UserSalary[]) => {
    const grouped: Record<string, UserSalary[]> = {};
    salaries.forEach((salary) => {
      const first = salary.username?.trim() || 'Desconocido';
      const last = salary.last_name?.trim() || '';
      const fullName = `${first} ${firstLetterFirstLastName(last)}`.trim();
      if (!grouped[fullName]) {
        grouped[fullName] = [];
      }
      grouped[fullName].push(salary);
    });
    return grouped;
  };

  const groupedSalaries = groupByFullName(filteredSalaries);
  const groupedUsers = Object.entries(groupedSalaries);

  // ========================= CREAR SERIES (LINECHART) =========================
  const createSeriesByMonthYear = (
    salaries: UserSalary[],
    index: number,
    label: string
  ) => {
    const dataByMonthYear = distinctMonthYears.map(({ raw }) => {
      const monthlyTotal = salaries
        .filter((s) => s.month_year === raw)
        .reduce((sum, s) => sum + (s.total_payed ?? 0), 0);
      return monthlyTotal;
    });

    return {
      data: dataByMonthYear,
      label,
      connectNulls: true,
    };
  };

  const series = groupedUsers.map(([fullName, userSalaries], index) =>
    createSeriesByMonthYear(userSalaries, index, fullName)
  );

  // ========================= SERIE COMBINADA =========================
  const combinedSeries = {
    id: 'combined',
    color: '#FC7556',
    data: distinctMonthYears.map(({ raw }) => {
      return filteredSalaries
        .filter((s) => s.month_year === raw)
        .reduce((sum, s) => sum + (s.total_payed ?? 0), 0);
    }),
    label: 'Total',
    connectNulls: true,
  };

  // ========================= PIECHART =========================
  const totalGlobal = combinedSeries.data.reduce((acc, val) => acc + (val ?? 0), 0);
  const pieData = groupedUsers.map(([fullName, userSalaries]) => {
    const value = userSalaries.reduce((sum, s) => sum + (s.total_payed ?? 0), 0);
    const percentage = totalGlobal
      ? ((value / totalGlobal) * 100).toFixed(2)
      : '0';
    return {
      id: `user-${fullName}`,
      label: `${fullName} (${percentage}%)`,
      value,
    };
  });

  // ========================= MEMOIZAR CÁLCULOS DE CARDS =========================
  const {
    totalCurrentMonth,
    averageExpense,
    variationPercent,
    isNegative,
  } = useMemo(() => {
    const today = new Date();
    const thisMonth = String(today.getMonth() + 1).padStart(2, '0');
    const thisYearShort = String(today.getFullYear() % 100).padStart(2, '0');
    const currentMonthYear = `${thisMonth}/${thisYearShort}`;

    const currentMonthSalaries = salaryList.filter(
      (s) => s.month_year === currentMonthYear
    );
    const totalCurrentMonth = currentMonthSalaries.reduce(
      (acc, s) => acc + (s.total_payed ?? 0),
      0
    );
    const uniqueUsers = new Set(currentMonthSalaries.map((s) => s.user_id))
      .size;
    const averageExpense =
      uniqueUsers > 0 ? totalCurrentMonth / uniqueUsers : 0;

    // Variación porcentual...
    let variationPercent = 0;
    let isNegative = false;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYearFull = now.getFullYear();
    const validMonths = distinctMonthYears
      .filter(
        ({ year, month }) =>
          year < currentYearFull ||
          (year === currentYearFull && month <= currentMonth)
      )
      .sort((a, b) => a.dateValue - b.dateValue);

    if (validMonths.length > 0) {
      const firstRaw = validMonths[0].raw;
      const firstTotal = salaryList
        .filter((s) => s.month_year === firstRaw)
        .reduce((acc, s) => acc + (s.total_payed ?? 0), 0);
      const totalInRange = salaryList
        .filter(({ month_year }) => {
          if (!month_year) return false;
          const { month, year } = parseMonthYear(month_year);
          return (
            year < currentYearFull ||
            (year === currentYearFull && month <= currentMonth)
          );
        })
        .reduce((acc, s) => acc + (s.total_payed ?? 0), 0);

      if (firstTotal !== 0) {
        variationPercent =
          ((totalInRange - firstTotal) / firstTotal) * 100;
        isNegative = variationPercent < 0;
      }
    }

    return {
      totalCurrentMonth,
      averageExpense,
      variationPercent,
      isNegative,
    };
  }, [salaryList, distinctMonthYears]);

  // ========================= RENDER =========================
  return (
    <div style={{ overflowX: 'hidden' }}>
      <CardSection
        totalCurrentMonth={totalCurrentMonth}
        averageExpense={averageExpense}
        variationPercent={variationPercent}
        isNegative={isNegative}
      />

      <ChartSection
        showCombined={showCombined}
        setShowCombined={setShowCombined}
        startYear={startYear}
        setStartYear={setStartYear}
        endYear={endYear}
        setEndYear={setEndYear}
        years={years}
        xAxisValues={xAxisValues}
        combinedSeries={combinedSeries}
        series={series}
        pieData={pieData}
      />
    </div>
  );
};

export default EstadisticasSalary;
